<template>
  <EquipmentBase
    :section-id="ScenarioDetailSectionKey.MOTOR"
    :class="{ 'compact-mode': !!stack }"
  >
    <!-- Compact Mode Layout -->
    <template v-if="stack">
      <!-- Header Row: Motor Title & Motor Performance Toggle -->
      <div class="compact-header-row" style="margin-top: 0.75rem">
        <div class="flex align-items-center">
          <h3 class="compact-title">Motor</h3>
          <CalculatorToggle :section-ids="motorCalculatorSectionKey" />
        </div>
        <div class="flex align-items-center">
          <LabelledToggle
            v-model="showMotorPerformance"
            text="Motor Performance"
          />
        </div>
      </div>

      <!-- Motor Performance Chart - Right below header -->
      <transition name="vertical-slide">
        <MotorPerformanceChart
          v-if="showMotorPerformance"
          enable-mouse-tracking
          :aspect-ratio="10"
          :minimum-height-px="325"
          :draw-legend="true"
          :is-horizontal="true"
        />
      </transition>

      <!-- Stats Row: HP Required, 60Hz Motor Required -->
      <div class="compact-header-row">
        <div class="flex align-items-center gap-2 compact-header-controls">
          <div class="flex align-items-center gap-1">
            <span class="compact-label">HP Required:</span>
            <span class="compact-value">
              {{ motorCalculations.totalRequiredBrakeHP.value.toFixed(0) }}
              HP
            </span>
          </div>
          <Divider layout="vertical" class="header-divider-compact" />
          <div class="flex align-items-center gap-1">
            <span class="compact-label">60Hz Motor Required:</span>
            <span class="compact-value">
              {{ motorCalculations.totalRequired60HzBrakeHP.value.toFixed(0) }}
              HP
            </span>
          </div>
        </div>
      </div>

      <Divider />

      <!-- Motor Row -->
      <div v-if="isEspOnShore" class="compact-motor-row-container">
        <MotorRow :stack="stack" />
      </div>
      <span v-else>
        <HpsMotorRow />
      </span>

      <!-- Motor Stats -->
      <div class="compact-section compact-stats-section">
        <div class="compact-section-header">Motor Stats</div>
        <div class="compact-section-content">
          <DataTable class="condensed" :value="motorStats">
            <Column field="field" />
            <Column
              bodyClass="numeral-4"
              field="nameplate"
              header="Nameplate"
            />
            <Column bodyClass="numeral-4" field="rerate" header="Rerate" />
          </DataTable>
        </div>
      </div>

      <!-- Temperatures - Below Stats, In Line -->
      <div
        v-if="isEspOnShore"
        class="compact-temperatures-row flex flex-wrap gap-4"
      >
        <ConverterInput
          label="Motor Winding Temperature"
          :model-value="motorCalculations.motorInternalTemperatureF.value"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          mode="convert"
          class="compact-temp-input"
        />
        <ConverterInput
          label="Motor Skin Temperature"
          :model-value="motorCalculations.motorSkinTemperatureF.value"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          mode="convert"
          class="compact-temp-input"
        />
        <ConverterInput
          label="Motor Temp Rating"
          :model-value="selectedMotor?.temperature_rating_fahrenheit || 0"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          mode="convert"
          class="compact-temp-input"
        />
      </div>
    </template>

    <!-- Full Mode Layout (default) -->
    <template v-else>
      <EquipmentHeader class="motor-header-full">
        <template #title>
          <div class="flex justify-content-between align-items-center">
            Motor
            <CalculatorToggle :section-ids="motorCalculatorSectionKey" />
          </div>
        </template>
        <template #titleRight>
          <div class="flex align-items-center gap-2 flex-wrap">
            <InlineValue
              :value="motorCalculations.totalRequiredBrakeHP.value.toFixed(0)"
              units="HP"
              title="HP Required"
              class="col-fixed"
            />
            <InlineValue
              :value="
                motorCalculations.totalRequired60HzBrakeHP.value.toFixed(0)
              "
              units="HP"
              title="60Hz Motor Required"
              class="col-fixed"
            />
            <Divider layout="vertical" class="header-divider" />
            <LabelledToggle
              v-model="showMotorPerformance"
              text="Motor Performance"
            />
          </div>
        </template>
      </EquipmentHeader>

      <transition name="vertical-slide">
        <MotorPerformanceChart
          v-if="showMotorPerformance"
          enable-mouse-tracking
          :aspect-ratio="10"
          :minimum-height-px="325"
          :draw-legend="true"
          :is-horizontal="true"
        />
      </transition>
      <span v-if="isEspOnShore">
        <MotorRow />
      </span>
      <span v-else>
        <HpsMotorRow />
      </span>

      <div class="field-grid">
        <div :class="{ 'field-col': isEspOnShore, 'col-2': !isEspOnShore }">
          <DataTable class="condensed" :value="motorStats">
            <Column field="field" />
            <Column
              bodyClass="numeral-4"
              field="nameplate"
              header="Nameplate"
            />
            <Column bodyClass="numeral-4" field="rerate" header="Rerate" />
          </DataTable>
        </div>
        <ConverterInput
          v-if="isEspOnShore"
          class="field-col"
          label="Motor Winding Temperature"
          :model-value="motorCalculations.motorInternalTemperatureF.value"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          mode="convert"
        />
        <ConverterInput
          v-if="isEspOnShore"
          class="field-col"
          label="Motor Skin Temperature"
          :model-value="motorCalculations.motorSkinTemperatureF.value"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          mode="convert"
        />
        <ConverterInput
          v-if="isEspOnShore"
          class="field-col"
          label="Motor Temp Rating"
          :model-value="selectedMotor?.temperature_rating_fahrenheit || 0"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          mode="convert"
        />
      </div>
    </template>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, provide, ref } from "vue";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { TemperatureUnits } from "@/libs/units/temperature";
import InlineValue from "@/components/scenarios/InlineValue.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";
import Divider from "primevue/divider";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  scenarioCalculationsKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
// import { useStackCalculations } from "@/composables/scenarioDetailProvider/useStackCalculations";
// import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import MotorPerformanceChart from "@/components/scenarios/charts/MotorPerformanceChart.vue";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import MotorRow from "@/components/equipment/motors/MotorRow.vue";
import HpsMotorRow from "@/components/equipment/hps_motors/HpsMotorRow.vue";
import { useSettingsStore } from "@/store";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type { StackLocation } from "@/components/equipment/d";

const props = defineProps<{
  stack?: StackLocation;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const { userSettingsNumberFormat } = useSettingsStore();

// Stack-aware motor selection
const selectedMotor = computed(() => {
  if (props.stack === "lower") {
    return equipment.value?.r_lower_motor;
  }
  return equipment.value?.r_motor;
});

// provide(
//   "selected-equipment-id",
//   computed(() => selectedMotor.value?.id)
// );
provide(
  "selected-equipment-id",
  computed(() => equipment.value?.r_motor?.id)
);

const sizingForm = injectStrict(sizingFormKey, null);

// Use stack-specific calculations (access motorCalculations directly from stackCalculations.value, no wrapping)
// const stackCalculations = useStackCalculations(props.stack || "upper");
// const scenarioCalculations = computed(
//   () => stackCalculations.value as ScenarioCalculations
// );
const { motorCalculations } = injectStrict(scenarioCalculationsKey);
const showMotorPerformance = ref(false);

const motorStats = computed(() => {
  return [
    {
      field: "HP",
      nameplate: motorCalculations.hpNameplate.value,
      rerate: Intl.NumberFormat(userSettingsNumberFormat.value, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      }).format(motorCalculations.adjustedReratedNameplateHP.value)
    },
    {
      field: "V",
      nameplate: motorCalculations.voltageNameplate.value,
      rerate: Intl.NumberFormat(userSettingsNumberFormat.value, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      }).format(motorCalculations.adjustedReratedNameplateV.value)
    },
    {
      field: "A",
      nameplate: motorCalculations.amperageNameplate.value,
      rerate: Intl.NumberFormat(userSettingsNumberFormat.value, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      }).format(motorCalculations.adjustedReratedNameplateA.value)
    }
  ];
});

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const motorCalculatorSectionKey = computed(() =>
  isEspOnShore.value
    ? [EquipmentCalculatorSectionKey.MOTOR]
    : [EquipmentCalculatorSectionKey.HPS_MOTOR]
);
</script>

<style lang="scss" scoped>
.motor-header-full {
  :deep(.equipment-header) {
    padding: 0.5rem 0 !important;
    gap: 1rem !important;
    margin-bottom: 0.5rem;
    grid-template-columns: minmax(20px, max-content) 1fr !important;

    .left {
      flex-shrink: 0;
    }

    .right {
      display: flex !important;
      flex: 1 1 auto;
      min-width: 0;
      justify-content: flex-start !important;
      align-items: center;
      gap: 1.5rem !important;
      flex-wrap: wrap;

      > * {
        flex: 0 0 auto;
      }

      .flex {
        gap: 1rem;
      }
    }
  }

  .header-divider {
    height: 2rem;
    margin: 0 0.5rem;
    opacity: 0.3;
  }
}

// Reduce padding for motor section
:deep(.equipment-row) {
  padding-top: 1em !important;
}
</style>
